import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, Subject, BehaviorSubject } from 'rxjs';
import { AppSettings } from './app.settings';

@Injectable({
  providedIn: 'root'
})
export class InventoryService {
  headers: HttpHeaders;

  constructor(private http: HttpClient, private settings: AppSettings) {
    this.headers = new HttpHeaders().append('Content-Type', 'application/json');
  }

  public getAllBikeParts(): Observable<any> {
    return this.http.get(`${this.settings.api_url}/inventory/parts`);
  }

  public getAllBikeVariants(): Observable<any> {
    return this.http.get(`${this.settings.api_url}/inventory/variants`);
  }

  public getAllVariantsforPart(partId): Observable<any> {
    return this.http.get(`${this.settings.api_url}/inventory/parts/` + partId + `/variants`);
  }

  public getAllPartsAndVariants(): Observable<any> {
    return this.http.get<any>(`${this.settings.api_url}/inventory`);
  }

  public createPartVariant(partVariant): Observable<any> {
    let options = { headers: this.headers };

    // Build the payload that matches the stored procedure parameters
    const payload = {
      PartId: partVariant.PartId,
      VariantId: partVariant.VariantId || 0, // Send 0 for new variants
      VariantName: partVariant.VariantName, // Name includes cost format: ($10) VariantName
      Active: partVariant.Active,
      Quantity: partVariant.Quantity,
      WorkshopId: partVariant.WorkshopId,
      Cost: partVariant.Cost, // Cost is also stored separately in parts_variants table
      VariantImageUrl: partVariant.VariantImageUrl || null
    };

    return this.http.post(`${this.settings.api_url}/inventory`, payload, options);
  }

  public updatePartVariant(partVariant): Observable<any> {
    let options = { headers: this.headers };
    return this.http.put(`${this.settings.api_url}/inventory`, partVariant, options);
  }

  public updateBulkPartVariant(partVariant): Observable<any> {
    let options = { headers: this.headers };
    return this.http.put(`${this.settings.api_url}/inventory/bulk`, partVariant, options);
  }

  public getAllPartVariantsByFilter(workshopId): Observable<any> {
    return this.http.get<any>(`${this.settings.api_url}/inventory/workshop/` + workshopId);
  }

  public moveInventoryStock(moveInventoryDet): Observable<any> {
    let options = { headers: this.headers };
    return this.http.post(`${this.settings.api_url}/inventory/move`, moveInventoryDet, options);
  }

  public getAllPartDetails(): Observable<any> {
    return this.http.get(`${this.settings.api_url}/inventory/parts/details`);
  }

  public createPart(details): Observable<any> {
    let options = { headers: this.headers };
    return this.http.post(`${this.settings.api_url}/inventory/parts`, details, options);
  }

  public updatePart(part: any): Observable<any> {
    let options = { headers: this.headers };
    return this.http.put(`${this.settings.api_url}/inventory/parts/${part.PartId}`, part, options);
  }

  public updatePartTranslation(key: string, enValue: string, noValue: string): Observable<any> {
    let options = { headers: this.headers };
    return this.http.post(`${this.settings.api_url}/translation/part`, { Key: key, English: enValue, Norwegian: noValue }, options);
  }

  public deletePartVariant(id: number): Observable<any> {
    return this.http.delete(`${this.settings.api_url}/inventory/` + id);
  }

  // Subject to notify components when inventory data needs refreshing
  public onInventoryUpdated = new Subject<void>();

  // BehaviorSubject to track if seeding is in progress (for loading indicators)
  public isSeeding$ = new BehaviorSubject<boolean>(false);

  public uploadImage(file: File, folder: string): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    // Note: Do NOT use this.headers here because HttpClient must set Content-Type to multipart/form-data with boundary automatically
    return this.http.post(`${this.settings.api_url}/inventory/upload/${folder}`, formData);
  }

  public seedWorkshopsRecursively(partId: number, variantId: number, quantity: number, cost: number, workshops: any[]): void {
    if (workshops.length === 0) {
      console.log('InventoryService: All workshops seeded successfully. Triggering refresh.');
      // Notify components to refresh data
      this.onInventoryUpdated.next();
      this.isSeeding$.next(false);
      return;
    }

    // If this is the start of a sequence (or ongoing), ensure we flag it
    if (!this.isSeeding$.value) {
      this.isSeeding$.next(true);
    }

    const workshop = workshops[0];
    const payload = {
      PartId: partId,
      VariantId: variantId,
      WorkshopId: workshop.Id,
      Quantity: quantity,
      Cost: cost,
      Active: true
    };

    this.createPartVariant(payload).subscribe(
      () => {
        console.log(`InventoryService: ✓ Successfully seeded workshop: ${workshop.Name}`);
        this.seedWorkshopsRecursively(partId, variantId, quantity, cost, workshops.slice(1));
      },
      (error) => {
        console.error(`InventoryService: ✗ Failed to seed workshop: ${workshop.Name}`, error);
        this.seedWorkshopsRecursively(partId, variantId, quantity, cost, workshops.slice(1));
      }
    );
  }
}
